import {
  isNumeric,
  removeNumberFormatting,
} from '@shared/helpers/math.helpers';

/** British English grouping and decimal separators for import table display. */
const IMPORT_NUMBER_LOCALE = 'en-GB';

/**
 * Fraction-digit count after {@link removeNumberFormatting} (trailing zeros
 * kept so `1,50` stays two decimals).
 */
export const fractionDigitsInNormalizedNumber = (
  normalized: string,
): number => {
  const dot = normalized.indexOf('.');
  if (dot === -1) return 0;
  return normalized.length - dot - 1;
};

/**
 * Whether a raw CSV cell should be shown as a formatted number rather than
 * literal text. Plain digit-only strings are left as-is (resource codes, ids).
 */
export const shouldFormatImportTableCellAsNumber = (raw: string): boolean => {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  if (/^\d+$/.test(trimmed)) return false;
  return isNumeric(removeNumberFormatting(trimmed));
};

/** Format a numeric value in en-GB with an exact fractional width. */
export const formatImportGbNumber = (
  value: number,
  fractionDigits: number,
): string =>
  new Intl.NumberFormat(IMPORT_NUMBER_LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);

/**
 * Display text for one import metadata-strip cell. Preserves the decimal
 * precision of the imported CSV text; only switches locale layout to en-GB.
 */
export const formatImportTableCellDisplay = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (!shouldFormatImportTableCellAsNumber(trimmed)) return trimmed;
  const normalized = removeNumberFormatting(trimmed);
  if (!isNumeric(normalized)) return trimmed;
  const value = Number(normalized);
  if (!Number.isFinite(value)) return trimmed;
  return formatImportGbNumber(
    value,
    fractionDigitsInNormalizedNumber(normalized),
  );
};
